import { amsterdamMinuutVanDag } from './amsterdamTijd';
import type { HandlingTimeWorkWindowRow } from './handlingTime.api';

/**
 * Behandeltijd-rapport, CAP-2 en CAP-3 — het rekenwerk van het werkvenster en het gat.
 *
 * Zuiver, zonder React. De regels staan in de Vastgelegde uitgangspunten van de SPEC: de route geeft
 * per persoon per dag alleen het venster en het aantal gat-minuten, en alles wat over een periode of
 * een persoon gaat wordt hier opgeteld.
 */

export type WerkvensterDag = HandlingTimeWorkWindowRow;

/** Het eind van een dag die over middernacht is geknipt: 24:00, minuten sinds middernacht. */
const MIDDERNACHT_EIND = 24 * 60;

/** Het antwoord per persoon over de periode. Minuten van het begin en eind zijn minuten sinds middernacht. */
export interface WerkvensterSamenvatting {
  gatGemiddeldMinuten: number | null;
  gatPercentage: number | null;
  beginMediaan: number | null;
  eindMediaan: number | null;
}

/** Wat het scherm van de route weet: een antwoord, nog niet, of een storing. Nooit een nul als stand-in. */
export type WerkvensterWeergave =
  | { status: 'ok'; perPersoon: Map<number, WerkvensterSamenvatting> }
  | { status: 'laden' }
  | { status: 'fout' };

/** Een werkdag met een venster groter dan nul en een bekend gat. Een venster van nul of zonder eind telt niet mee. */
function heeftVenster(dag: WerkvensterDag): boolean {
  return dag.windowMinutes !== null && dag.windowMinutes > 0
    && dag.gapMinutes !== null && dag.windowStart !== null && dag.windowEnd !== null;
}

/** Half omhoog, op de hele eenheid. */
function halfOmhoog(waarde: number): number {
  return Math.floor(waarde + 0.5);
}

/** De mediaan van een lijst. Bij een even aantal het gemiddelde van de twee middelste, half omhoog op de minuut. */
export function mediaan(waarden: number[]): number | null {
  if (waarden.length === 0) return null;
  const gesorteerd = [...waarden].sort((a, b) => a - b);
  const midden = Math.floor(gesorteerd.length / 2);
  if (gesorteerd.length % 2 === 1) return gesorteerd[midden] ?? null;
  return halfOmhoog(((gesorteerd[midden - 1] ?? 0) + (gesorteerd[midden] ?? 0)) / 2);
}

/** Minuten sinds middernacht op de klok van Amsterdam, voor een tijdstip in UTC. */
export function klokMinuutVanDag(iso: string): number {
  return amsterdamMinuutVanDag(new Date(iso).getTime());
}

/** Minuten sinds middernacht als `UU:MM`; 1440 is 24:00, het eind van een geknipte dag. */
export function alsKlokuur(minuten: number): string {
  const uur = Math.floor(minuten / 60);
  const minuut = minuten % 60;
  return `${String(uur).padStart(2, '0')}:${String(minuut).padStart(2, '0')}`;
}

/**
 * De samenvatting van één persoon over de gekozen dagen.
 *
 * Gemiddeld gat per werkdag: de som van het gat gedeeld door het aantal werkdagen met een venster.
 * Gat-percentage over de periode: de som van het gat gedeeld door de som van het venster. Het
 * percentage wordt pas op één decimaal afgerond, uit de onafgeronde minuten.
 */
export function samenvatWerkvenster(dagen: WerkvensterDag[]): WerkvensterSamenvatting {
  const metVenster = dagen.filter(heeftVenster);
  // Begin, eind en gat komen uit DEZELFDE dagen: een venster van nul minuten is "—" (SPEC, Lege uitkomst),
  // ook in de eindmediaan.
  const gat = metVenster.reduce((som, dag) => som + (dag.gapMinutes ?? 0), 0);
  const venster = metVenster.reduce((som, dag) => som + (dag.windowMinutes ?? 0), 0);
  const beginnen = metVenster.map((dag) => klokMinuutVanDag(dag.windowStart ?? ''));
  // Een eind dat over middernacht is geknipt staat op Amsterdam-middernacht (SPEC, routecontract): dat is
  // het einde van de dag, 24:00, en niet het vroegste eind.
  const eindes = metVenster.map((dag) => {
    const minuut = klokMinuutVanDag(dag.windowEnd ?? '');
    return minuut === 0 ? MIDDERNACHT_EIND : minuut;
  });

  return {
    gatGemiddeldMinuten: metVenster.length === 0 ? null : halfOmhoog(gat / metVenster.length),
    gatPercentage: metVenster.length === 0 ? null : Math.floor((gat * 1000) / venster + 0.5) / 10,
    beginMediaan: mediaan(beginnen),
    eindMediaan: mediaan(eindes),
  };
}

/** Eén samenvatting per persoon: de dagen worden per persoon gegroepeerd, nooit over iedereen samen. */
export function samenvatPerPersoon(dagen: WerkvensterDag[]): Map<number, WerkvensterSamenvatting> {
  const perPersoon = new Map<number, WerkvensterDag[]>();
  for (const dag of dagen) {
    perPersoon.set(dag.userId, [...(perPersoon.get(dag.userId) ?? []), dag]);
  }
  return new Map([...perPersoon].map(([userId, eigen]) => [userId, samenvatWerkvenster(eigen)]));
}
